import {Component, OnDestroy, OnInit} from '@angular/core';
import {Router} from '@angular/router';
import {LangChangeEvent, TranslateService} from '@ngx-translate/core';
import {QueryRef} from 'apollo-angular';
import {Subscription} from 'rxjs';
import {BaseComponent} from '../../base/base.component';
import {UserService} from '../../service/user.service';
import {FilterService} from '../../service/filter.service';
import {
    Country,
    GetCountriesGQL,
    GetCountriesQuery,
    GetUnitiesByCountryGQL,
    GetUnitiesByCountryQuery,
    Unity,
    User
} from '../../../generated/graphql';
import {
    MOCK_DOCUMENT_AGE,
    MOCK_DOCUMENTS_BY_COUNTRY,
    MOCK_KPIS,
    MOCK_LANGUAGE_COVERAGE,
    MOCK_PUBLICATIONS_BY_QUARTER
} from './dashboard-policies.mock';

declare var moment;
declare var $;

interface KpiCard {
    label: string;
    value: string;
    hint: string;
    variant: 'default' | 'success' | 'warning' | 'danger';
}

@Component({
    selector: 'app-dashboard-policies',
    templateUrl: './dashboard-policies.component.html',
    styleUrls: ['./dashboard-policies.component.scss']
})
export class DashboardPoliciesComponent extends BaseComponent implements OnInit, OnDestroy {

    filterKey = 'dashboard_policies_filter';
    user: User = new User();

    subscription: Subscription;

    countries: Array<Country>;
    countryQuery: QueryRef<GetCountriesQuery>;

    unities: Array<Unity>;
    unityQuery: QueryRef<GetUnitiesByCountryQuery>;

    selectedCountryId: string;
    filter: any = {};

    kpiCards: KpiCard[] = [];

    // Módulo pobre em dado → 4 gráficos (o modelo não sustenta um 5º).
    myData1: any[] = [['', 0]];         // documentos por país
    myData2: any[] = [['', 0, 0, 0]];   // cobertura de idioma
    myData3: any[] = [['', 0]];         // publicações por trimestre
    myData4: any[] = [['', 0]];         // antiguidade dos documentos

    chart1Names = ['País', 'Documentos'];
    chart1NamesES = ['País', 'Documentos'];

    chart2Names = ['País', 'Só PT', 'Só ES', 'Ambos'];
    chart2NamesES = ['País', 'Solo PT', 'Solo ES', 'Ambos'];

    chart3Names = ['Trimestre', 'Publicações'];
    chart3NamesES = ['Trimestre', 'Publicaciones'];

    chart4Names = ['Faixa', 'Documentos'];
    chart4NamesES = ['Rango', 'Documentos'];

    columnNames1: string[] = [];
    columnNames2: string[] = [];
    columnNames3: string[] = [];
    columnNames4: string[] = [];

    options1 = {legend: {position: 'none'}, vAxis: {minValue: 0, format: '0', gridlines: {count: -1}}, colors: ['#032E58']};
    // Cobertura de idioma: expõe o gap de tradução (Só PT em destaque).
    options2 = {
        isStacked: 'percent',
        legend: {position: 'top'},
        vAxis: {minValue: 0, maxValue: 1, format: 'percent', gridlines: {count: 5}},
        colors: ['#FAE100', '#004B93', '#032E58']
    };
    options3 = {legend: {position: 'none'}, vAxis: {minValue: 0, format: '0', gridlines: {count: -1}}, colors: ['#004B93']};
    // Antiguidade: +24 meses em VERMELHO (documento desatualizado = alerta).
    options4 = {legend: {position: 'right'}, colors: ['#032E58', '#004B93', '#FAE100', '#C0392B']};

    constructor(public router: Router,
                public translate: TranslateService,
                public userService: UserService,
                private getCountriesGQL: GetCountriesGQL,
                private getUnitiesByCountryGQL: GetUnitiesByCountryGQL,
                private filterService: FilterService) {
        super(router, translate);
        this.subscription = this.translate.onLangChange.subscribe((event: LangChangeEvent) => {
            $(() => {
                this.checkSubtitle();
                this.buildKpis();
                this.buildCharts();
            });
        });
    }

    ngOnInit(): void {
        this.userService.user.subscribe({
            next: data => {
                this.user = data as User;
            }, error: err => super.onError(err)
        });

        this.getCountries();
        this.getUnitiesInit();

        const savedFilter = this.filterService.getFilter(this.filterKey);
        if (savedFilter) {
            this.filter = savedFilter;
            this.selectedCountryId = savedFilter.countryId;
            if (savedFilter.countryId) {
                this.getUnities();
            }
        } else {
            this.setDefaultPeriod();
        }

        this.initDatePicker();

        this.checkSubtitle();
        this.buildKpis();
        this.buildCharts();
    }

    ngOnDestroy(): void {
        this.subscription.unsubscribe();
    }

    setDefaultPeriod() {
        this.filter.start = moment().startOf('day').subtract(30, 'days').utcOffset(0, true).format();
        this.filter.end = moment().endOf('day').utcOffset(0, true).format();
    }

    initDatePicker() {
        this.setDatePickerRange('dashboard-daterangepicker', (start, end, label) => {
            this.filter.start = start.utcOffset(0, true).format();
            this.filter.end = end.utcOffset(0, true).format();
        }, this.filter.start, this.filter.end);
    }

    getCountries() {
        this.countryQuery = this.getCountriesGQL.watch();
        this.countryQuery.valueChanges.subscribe(({data}) => {
            this.countries = data.countries as Array<Country>;
        });
    }

    getUnitiesInit() {
        this.unityQuery = this.getUnitiesByCountryGQL.watch({countryId: this.isNullOrUndefined(this.selectedCountryId) ? '' : this.selectedCountryId});
        this.unityQuery.valueChanges.subscribe(({data}) => {
            this.unities = data.getUnitiesByCountry as Array<Unity>;
        });
    }

    getUnities() {
        this.unityQuery.refetch({countryId: this.isNullOrUndefined(this.selectedCountryId) ? '' : this.selectedCountryId});
    }

    applyFilter() {
        if (!this.isNullOrUndefined(this.selectedCountryId)) {
            this.filter.countryId = this.selectedCountryId;
        } else {
            this.filter.countryId = undefined;
        }
        this.filterService.setFilter(this.filterKey, this.filter);

        super.toggleLoader(true);
        this.buildKpis();
        this.buildCharts();
        super.toggleLoader(false);
        // TODO(dados-reais): aplicar filtro
    }

    clearFilter() {
        this.filterService.clearFilter(this.filterKey);
        this.filter = {};
        this.selectedCountryId = null;
        this.setDefaultPeriod();
    }

    // ---------------------------------------------------------------------
    buildKpis() {
        const k = MOCK_KPIS;
        const t = (key: string) => this.translate.instant('dashboards.policies.' + key);
        this.kpiCards = [
            {label: t('kpi.totalDocuments'), value: this.fmtInt(k.totalDocuments), hint: '', variant: 'default'},
            {label: t('kpi.countriesCovered'), value: this.fmtInt(k.countriesCovered), hint: '', variant: 'default'},
            {label: t('kpi.availablePt'), value: this.fmtInt(k.availablePt), hint: '', variant: 'default'},
            {label: t('kpi.availableEs'), value: this.fmtInt(k.availableEs), hint: '', variant: 'default'},
            {label: t('kpi.missingEs'), value: this.fmtInt(k.missingEsTranslation), hint: '', variant: 'warning'},
            {label: t('kpi.stale'), value: this.fmtInt(k.staleOver12Months), hint: '', variant: 'danger'},
        ];
    }

    buildCharts() {
        this.myData1 = MOCK_DOCUMENTS_BY_COUNTRY.map(r => [r.country, r.count]);
        this.myData2 = MOCK_LANGUAGE_COVERAGE.map(r => [r.country, r.onlyPt, r.onlyEs, r.both]);
        this.myData3 = MOCK_PUBLICATIONS_BY_QUARTER.map(r => [r.quarter, r.count]);
        this.myData4 = MOCK_DOCUMENT_AGE.map(r => [this.ageLabel(r.range), r.count]);
    }

    checkSubtitle() {
        if (this.translate.currentLang === 'pt') {
            this.columnNames1 = this.chart1Names;
            this.columnNames2 = this.chart2Names;
            this.columnNames3 = this.chart3Names;
            this.columnNames4 = this.chart4Names;
        } else {
            this.columnNames1 = this.chart1NamesES;
            this.columnNames2 = this.chart2NamesES;
            this.columnNames3 = this.chart3NamesES;
            this.columnNames4 = this.chart4NamesES;
        }
    }

    ageLabel(range: string): string {
        return this.translate.instant('dashboards.policies.age.' + range);
    }

    fmtInt(n: number): string {
        return n.toString();
    }
}
